import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography, Button, IconButton, Paper, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useMediaQuery from '@mui/material/useMediaQuery';

import { AppBgColor } from '../../config/DiactiveTheme';
import { submitQuestionnaire, fetchQuestionnaire } from '../../services/QuestionnaireService';
import { submitQuestionnaireWithToken } from '../../services/FunctionService';
import { QuestionnaireMode, UnityPaths } from '../../config/DiactiveTypes';
import { isEmpty, formatTimestamp } from '../../utils/DiactiveUtils';
import QuestionsTable from './QuestionsTable';
import QuestionList from './QuestionList';
import ConfirmDialog from '../common/ConfirmDialog';
import QuestionnaireDefinitions from '../../data/questionnaires/QuestionnaireDefinitions';

const answerByMsgs = {
  "patient": "Este cuestionario debe ser respondido por ti.",
  "parent": "Este cuestionario debe ser respondido por tu padre/madre."
};

const Questionnaire = ({ mode, questionnaireId, phase, patientId, token }) => {
  const navigate = useNavigate();
  const questionnaire = QuestionnaireDefinitions[questionnaireId];

  const [answers, setAnswers] = useState(() =>
    questionnaire.questions.map(q =>
      q.items ? Array(q.items.length).fill(undefined) : undefined
    )
  );
  const [userQuestionnaire, setUserQuestionnaire] = useState(null);
  const [dialogConfig, setDialogConfig] = useState({ open: false, title: "", description: "", confirmAction: null, confirmButtonColor: "primary" });
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const hasSomeAnswers = answers.some((answer) =>
    Array.isArray(answer)
      ? answer.some((a) => !isEmpty(a))
      : !isEmpty(answer)
  );

  const hasAllAnswers = answers.every((answer) =>
    Array.isArray(answer)
      ? answer.every((a) => !isEmpty(a))
      : !isEmpty(answer)
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mode === QuestionnaireMode.View) {
      const fetchUserQuestionnaire = async () => {
        const filledQuestionnaire = await fetchQuestionnaire(phase, questionnaireId, patientId);
        if (filledQuestionnaire) {
          setUserQuestionnaire(filledQuestionnaire);
        }
      };
      fetchUserQuestionnaire();
    }
  }, []);

  const handleAnswerChange = (qIndex, value, itemIndex = null) => {
    if (!isNaN(value)) value = parseFloat(value);

    setAnswers((prev) => {
      const updatedAnswers = [...prev];
      if (itemIndex === null) {
        updatedAnswers[qIndex] = value;
      } else {
        if (!Array.isArray(updatedAnswers[qIndex])) {
          updatedAnswers[qIndex] = [];
        }
        updatedAnswers[qIndex][itemIndex] = value;
      }
      return updatedAnswers;
    });
  };

  const handleBack = () => {
    if (mode === QuestionnaireMode.View) {
      navigate(`/patient/view/${patientId}/${phase}`);
      return;
    }

    if (mode === QuestionnaireMode.Embed_Fill) {
      window.location.href = `uniwebview://${UnityPaths.QuestionnaireBack}`;
      return;
    }

    if (hasSomeAnswers) {
      setDialogConfig({
        open: true,
        title: questionnaire.name,
        description: "¿Seguro que quieres salir? Tus respuestas no se guardarán.",
        confirmAction: handleConfirmBack,
        confirmButtonColor: "error",
      });
    }
    else {
      navigate("/");
    }
  };

  const handleSubmit = () => {
    if (!hasAllAnswers) {
      setErrorMessage("Por favor, completa todas las preguntas antes de enviar.");
      return;
    }

    for (let questionIndex = 0; questionIndex < questionnaire.questions.length; questionIndex++) {
      const question = questionnaire.questions[questionIndex];
      const answer = answers[questionIndex];

      if (question.type === "textarea" && (!answer || answer.length < question.charLimit.min)) {
        setErrorMessage(
          `Por favor, escribe al menos ${question.charLimit.min} caracteres en la pregunta ${questionIndex+1}.`
        );
        return;
      }
    }

    setErrorMessage(null);
    setDialogConfig({
      open: true,
      title: "Confirmar envío",
      description: "¿Estás seguro de que quieres enviar tus respuestas? Asegúrate que sean correctas, ya que no podrás modificarlas después.",
      confirmAction: handleConfirmSubmit,
      confirmButtonColor: "success",
    });
  };

  const handleConfirmBack = () => {
    setDialogConfig({ ...dialogConfig, open: false });
    navigate("/");
  };

  const handleConfirmSubmit = async () => {
    setDialogConfig({ ...dialogConfig, open: false });
    setSubmitting(true);

    try {
      if (mode === QuestionnaireMode.Embed_Fill) {
        const submitted = await submitQuestionnaireWithToken(token, answers);
        if (submitted) {
          window.location.href = `uniwebview://${UnityPaths.QuestionnaireSubmitted}`;
        }
        else {
          alert("Error al enviar el cuestionario. Por favor, inténtalo de nuevo.");
          setSubmitting(false);
        }
      }
      else {
        await submitQuestionnaire(questionnaireId, answers, phase);
        navigate("/");
      }
    }
    catch (error) {
      setSubmitting(false);
      setErrorMessage(error.message);
      setTimeout(() => window.scrollTo(0, document.body.scrollHeight), 50);
    }
  };

  const handleCancelDialog = () => {
    setDialogConfig({ ...dialogConfig, open: false });
  };

  const isDesktop = useMediaQuery('(min-width:900px)');
  const layoutBlocks = getQuestionnaireLayoutTypes(questionnaire.questions, isDesktop);

  const renderQuestions = () => {
    let questionOffset = 0;
    return layoutBlocks.map((block, i) => {
      const currentOffset = questionOffset;
      const allAnswers = mode === QuestionnaireMode.View ? userQuestionnaire.answers : answers;
      const blockAnswers = allAnswers.slice(currentOffset, currentOffset + block.questions.length);

      const blockProps = {
        questions: block.questions,
        answerOptions: questionnaire.answerOptions,
        answers: blockAnswers,
        whiteSpace: questionnaire.whiteSpace,
        questionOffset: currentOffset,
      };
      if (mode === QuestionnaireMode.View)  {
        blockProps.onAnswerChange = null;
      }
      else {
        blockProps.onAnswerChange = (qIndex, value, itemIndex) =>
          handleAnswerChange(qIndex + currentOffset, value, itemIndex);
      }
      questionOffset += block.questions.length;

      switch (block.type) {
        case "table":
          return <QuestionsTable key={i} {...blockProps} />;
        default:
          return <QuestionList key={i} {...blockProps} />;
      }
    });
  }

  if (mode === QuestionnaireMode.View && !userQuestionnaire) return null;

  const backgroundColor = mode === QuestionnaireMode.Embed_Fill ? AppBgColor : "transparent";
  const progressColor = mode === QuestionnaireMode.Embed_Fill ? '#fff' : 'default';

  if (submitting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor }}>
        <CircularProgress size={45} sx={{ color: progressColor }} />
      </Box>
    );
  }

  return (
    <Container maxWidth={questionnaire.maxWidth} sx={{ pt: 4, pb: 6, backgroundColor }}>
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBackIcon color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: 2 }}>
            <Typography variant="h5" component="h1">
              {questionnaire.name}
            </Typography>
            {
              mode === QuestionnaireMode.View &&
              <Typography component="p">
                {`Respondido: ${formatTimestamp(userQuestionnaire.createdAt)}`}
              </Typography>
            }
          </Box>
        </Box>
        {
          mode !== QuestionnaireMode.View &&
          <>
            <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 1 }}>
              {answerByMsgs[questionnaire.target]}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {questionnaire.description}
            </Typography>
          </>
        }
        <Box component="form">
          {renderQuestions()}
          {
            errorMessage && (
              <Typography variant="body2" color="error" sx={{display: 'flex', justifyContent: 'center', mt: -2, mb: -2}}>
                {errorMessage}
              </Typography>
            )
          }
          {
            mode !== QuestionnaireMode.View &&
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Button variant="contained" color="primary" onClick={handleSubmit}>
                Enviar
              </Button>
            </Box>
          }
        </Box>
      </Paper>
      {
        mode !== QuestionnaireMode.View &&
        <ConfirmDialog
          open={dialogConfig.open}
          title={dialogConfig.title}
          description={dialogConfig.description}
          onCancel={handleCancelDialog}
          onConfirm={dialogConfig.confirmAction}
          confirmButtonColor={dialogConfig.confirmButtonColor}
        />
      }
    </Container>
  );
};

const getQuestionnaireLayoutTypes = (questions, isDesktop) => {
  if (!isDesktop) {
    // On mobile, treat everything as a list
    return [{ type: "list", questions }];
  }

  const result = [];
  let currentType = null;
  let currentGroup = [];

  const getType = q => {
    if (q.type === "table") return "table";
    return "list";
  };

  questions.forEach(q => {
    const type = getType(q);
    if (type !== currentType) {
      if (currentGroup.length) {
        result.push({ type: currentType, questions: currentGroup });
      }
      currentType = type;
      currentGroup = [q];
    } else {
      currentGroup.push(q);
    }
  });
  if (currentGroup.length) {
    result.push({ type: currentType, questions: currentGroup });
  }
  return result;
};

export default Questionnaire;